import { format } from "date-fns";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useApi } from "../hooks/useApi";

interface DetailItem {
  FromTo: string;
  Hrs: number;
  Lateral: number;
  Phase: string;
  Cat: string;
  MajorOP: string;
  Action: string;
  Object: string;
  Responsible: string;
  Summary: string;
  Content: string;
  Rig: string;
  Date: string;
  Well: string;
  FileUrl: string;
  [key: string]: any;
}

interface LostTimeDetailItem {
  Type: string;
  Cause: string;
  Object: string;
  Responsible: string;
  Depth: number;
  Summary: string;
  CumHrs: number;
  From: string;
  Hrs: number;
  LtId: number;
  ParentLtId: number;
  Rig: string;
  Date: string;
  Well: string;
  FileUrl: string;
  [key: string]: any;
}

interface Report {
  Id: string;
  Rig: string;
  Date: string;
  Well: string;
  Last24: string;
  Next24: string;
  Foreman: string;
  Engineer: string;
  Manager: string;
  FileUrl: string;
  Details?: DetailItem[];
  LostTimeDetails?: LostTimeDetailItem[];
  [key: string]: any;
}

interface SearchContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  pendingSearchTerm: string;
  setPendingSearchTerm: (term: string) => void;
  triggerSearch: () => void;
  clearSearch: () => void;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  // Location functionality
  availableLocations: string[];
  selectedLocation: string | null;
  setSelectedLocation: (location: string | null) => void;
  locationLoading: boolean;
  locationError: string | null;
  // Data functionality
  details: DetailItem[];
  lostTimeDetails: LostTimeDetailItem[];
  reports: Report[];
  loading: boolean;
  error: string | null;
  counts: {
    totalReports: number;
    totalDetails: number;
    totalLostTimeDetails: number;
    filteredReports: number;
    filteredDetails: number;
    filteredLostTimeDetails: number;
  };
  fetchData: () => Promise<void>;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pendingSearchTerm, setPendingSearchTerm] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Location state
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Data state
  const [details, setDetails] = useState<DetailItem[]>([]);
  const [lostTimeDetails, setLostTimeDetails] = useState<LostTimeDetailItem[]>(
    []
  );
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState({
    totalReports: 0,
    totalDetails: 0,
    totalLostTimeDetails: 0,
    filteredReports: 0,
    filteredDetails: 0,
    filteredLostTimeDetails: 0,
  });

  const { callApi } = useApi();

  // Track if request is in progress to prevent race conditions
  const requestInProgressRef = useRef(false);

  const handleSetSearchTerm = useCallback((term: string | undefined | null) => {
    setSearchTerm(term || ""); // Ensure we never set undefined or null
  }, []);

  const handleSetPendingSearchTerm = useCallback(
    (term: string | undefined | null) => {
      setPendingSearchTerm(term || ""); // Ensure we never set undefined or null
    },
    []
  );

  const triggerSearch = useCallback(() => {
    setSearchTerm(pendingSearchTerm);
  }, [pendingSearchTerm]);

  const clearSearch = useCallback(() => {
    setPendingSearchTerm("");
    setSearchTerm("");
  }, []);

  // Helpers for multi-word search
  const escapeRegex = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const tokenizeSearch = (value: string): string[] => {
    const trimmed = (value || "").trim().toLowerCase();
    if (!trimmed) return [];
    // Support quoted phrases and space-separated terms
    const tokens: string[] = [];
    const regex = /\"([^\"]+)\"|([^\s]+)/g; // "phrase with spaces" or singleWord
    let match: RegExpExecArray | null;
    while ((match = regex.exec(trimmed)) !== null) {
      const phrase = (match[1] || match[2] || "").trim();
      if (phrase.length > 0) tokens.push(phrase);
    }
    return tokens;
  };
  const objectToSearchString = (obj: Record<string, any>): string => {
    return Object.entries(obj)
      .filter(
        ([key, value]) =>
          key !== "Details" &&
          key !== "LostTimeDetails" &&
          value !== null &&
          value !== undefined
      )
      .map(([, value]) => String(value).toLowerCase())
      .join(" ");
  };

  // Data fetching functionality
  const fetchData = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (requestInProgressRef.current) {
      console.log("🚫 Request already in progress, skipping");
      return;
    }

    try {
      requestInProgressRef.current = true;
      setLoading(true);
      setError(null);

      console.log("🚀 Fetching data for:", {
        searchTerm,
        selectedDate: format(selectedDate, "yyyy-MM-dd"),
        selectedLocation,
      });

      // Make HTTP request
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      const locationParam = selectedLocation
        ? `&location=${encodeURIComponent(selectedLocation)}`
        : "";
      // Important: fetch unfiltered by searchTerm so client-side multi-word AND filtering is authoritative
      const fullUrl = `/api/Reports/table?reportDate=${formattedDate}${locationParam}`;
      console.log("📡 Request URL:", fullUrl);
      console.log("🔍 Search term being sent to API:", `"${searchTerm}"`);

      const response = await callApi(fullUrl);
      console.log("📡 HTTP Response status:", response.status, response.ok);

      if (!response.ok) {
        throw new Error("Failed to fetch reports");
      }

      const data = (await response.json()) as Report[];
      console.log("📊 Received data:", data.length, "reports");

      // Process details data
      const processedDetails = data.flatMap((report) => {
        const details = report.Details || [];
        return details.map((detail) => ({
          FromTo: detail.fromTo || "",
          Hrs: detail.hrs || 0,
          Lateral: detail.lateral || 0,
          Phase: detail.phase || "",
          Cat: detail.cat || "",
          MajorOP: detail.majorOP || "",
          Action: detail.action || "",
          Object: detail.object || "",
          Responsible: detail.responsible || "",
          Summary: detail.summary || "",
          Content: detail.content || "",
          Rig: report.Rig || "",
          Date: report.Date || "",
          Well: report.Well || "",
          FileUrl: report.FileUrl || "",
        }));
      });

      // Process lost time details data
      const processedLostTimeDetails = data.flatMap((report) => {
        const details = report.LostTimeDetails || [];
        return details.map((detail) => ({
          Type: detail.type || "",
          Cause: detail.cause || "",
          Object: detail.object || "",
          Responsible: detail.responsible || "",
          Depth:
            typeof detail.depth === "string"
              ? parseFloat(detail.depth) || 0
              : detail.depth || 0,
          Summary: detail.summary || "",
          CumHrs:
            typeof detail.cumHrs === "string"
              ? parseFloat(detail.cumHrs) || 0
              : detail.cumHrs || 0,
          From: detail.from || "",
          Hrs: typeof detail.hrs === "number" ? detail.hrs : detail.hrs || 0,
          LtId:
            typeof detail.ltId === "number" ? detail.ltId : detail.ltId || 0,
          ParentLtId:
            typeof detail.parentLtId === "string"
              ? parseInt(detail.parentLtId) || 0
              : detail.parentLtId || 0,
          Rig: report.Rig || "",
          Date: report.Date ? report.Date.split("T")[0] : "",
          Well: report.Well || "",
          FileUrl: report.FileUrl || "",
        }));
      });

      // Filter data if there's a search term (multi-word AND semantics, supports quoted phrases)
      const tokens = tokenizeSearch(searchTerm);

      const filteredDetails =
        tokens.length > 0
          ? processedDetails.filter((detail) => {
              const haystack = objectToSearchString(detail);
              return tokens.every((token) => haystack.includes(token));
            })
          : processedDetails;

      const filteredLostTimeDetails =
        tokens.length > 0
          ? processedLostTimeDetails.filter((detail) => {
              const haystack = objectToSearchString(detail);
              return tokens.every((token) => haystack.includes(token));
            })
          : processedLostTimeDetails;

      const filteredReports =
        tokens.length > 0
          ? data.filter((report) => {
              const reportHaystack = objectToSearchString(report as any);

              // Build detail haystacks for both Details and LostTimeDetails if present
              const detailHaystacks: string[] = [];
              if (
                (report as any).Details &&
                Array.isArray((report as any).Details)
              ) {
                detailHaystacks.push(
                  ...(report as any).Details.map((d: any) =>
                    objectToSearchString(d)
                  )
                );
              }
              if (
                (report as any).LostTimeDetails &&
                Array.isArray((report as any).LostTimeDetails)
              ) {
                detailHaystacks.push(
                  ...(report as any).LostTimeDetails.map((d: any) =>
                    objectToSearchString(d)
                  )
                );
              }

              // A report matches if for every token, it appears either in the report itself or in any of its details
              return tokens.every((token) => {
                if (reportHaystack.includes(token)) return true;
                return detailHaystacks.some((h) => h.includes(token));
              });
            })
          : data;

      // Update counts
      const newCounts = {
        totalReports: data.length,
        totalDetails: processedDetails.length,
        totalLostTimeDetails: processedLostTimeDetails.length,
        filteredReports: filteredReports.length,
        filteredDetails: filteredDetails.length,
        filteredLostTimeDetails: filteredLostTimeDetails.length,
      };

      // Update state
      console.log("🔄 Updating state with fresh data");
      setReports(filteredReports);
      setDetails(filteredDetails);
      setLostTimeDetails(filteredLostTimeDetails);
      setCounts(newCounts);
    } catch (err) {
      console.error("❌ Error in fetchData:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch report data"
      );
    } finally {
      setLoading(false);
      requestInProgressRef.current = false;
    }
  }, [searchTerm, callApi, selectedDate, selectedLocation]);

  // Location functionality
  const fetchLocations = useCallback(async () => {
    try {
      setLocationLoading(true);
      setLocationError(null);

      const response = await callApi("/api/User/locations");

      if (!response.ok) {
        throw new Error("Failed to fetch user locations");
      }

      const locations = (await response.json()) as string[];
      setAvailableLocations(locations);

      // Auto-select location based on availability
      if (locations.length === 1) {
        // If only one location, use it as default
        setSelectedLocation(locations[0]);
      } else if (locations.length > 1) {
        // If multiple locations, select the first one as default
        setSelectedLocation(locations[0]);
      } else {
        // No locations available
        setSelectedLocation(null);
      }
    } catch (err) {
      console.error("Error fetching user locations:", err);
      setLocationError(
        err instanceof Error ? err.message : "Failed to fetch user locations"
      );
      setAvailableLocations([]);
      setSelectedLocation(null);
    } finally {
      setLocationLoading(false);
    }
  }, [callApi]);

  // Fetch locations on mount
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Trigger fetch when any parameter changes, but only after locations are loaded
  useEffect(() => {
    if (!selectedLocation) return;

    console.log("🎯 Parameters changed, triggering fetch:", {
      searchTerm,
      selectedDate: format(selectedDate, "yyyy-MM-dd"),
      selectedLocation,
    });
    console.log("🔍 Search term specifically changed to:", `"${searchTerm}"`);
    const timeoutId = setTimeout(() => {
      fetchData();
    }, 100); // Small delay to debounce rapid changes

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedDate, selectedLocation, fetchData]);

  const handleSetSelectedLocation = useCallback((location: string | null) => {
    setSelectedLocation(location);
  }, []);

  return (
    <SearchContext.Provider
      value={{
        searchTerm,
        setSearchTerm: handleSetSearchTerm,
        pendingSearchTerm,
        setPendingSearchTerm: handleSetPendingSearchTerm,
        triggerSearch,
        clearSearch,
        selectedDate,
        setSelectedDate,
        availableLocations,
        selectedLocation,
        setSelectedLocation: handleSetSelectedLocation,
        locationLoading,
        locationError,
        // Data properties
        details,
        lostTimeDetails,
        reports,
        loading,
        error,
        counts,
        fetchData,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};
